import type { ApolloClient } from '@apollo/client';
import { GET_ORG_CALL_BY_ID } from '../../../lib/graphql/queries/organizations';
import { type CallRecord } from './CallDetailsModal';

/**
 * Map a GQL call payload (GET_ORG_CALL_BY_ID) to the CallRecord shape the
 * CallDetailsModal expects. Single source of truth for the call-by-id
 * adapter (RecentActivitiesTable and ActivityTab keep older local copies).
 */
export function adaptGqlCallToCallRecord(c: Record<string, unknown>): CallRecord {
  const tryParse = (v: unknown) => {
    if (typeof v !== 'string') return undefined;
    try {
      return JSON.parse(v);
    } catch {
      return undefined;
    }
  };
  const timeline = (() => {
    const parsed = tryParse(c.callAnalysisTimeline);
    return Array.isArray(parsed) ? parsed : undefined;
  })();
  const answers = tryParse(c.callAnalysisAnswers);
  return {
    id: c.callId as string,
    call_id: c.callId as string,
    inbound: (c.inbound as boolean) ?? false,
    callTimestamp: c.initiatedAt ? new Date(c.initiatedAt as string) : undefined,
    duration: (c.durationSeconds as number) ?? 0,
    corrected_duration: (c.durationSeconds as number) ?? 0,
    answered_by: (c.answeredBy as CallRecord['answered_by']) ?? undefined,
    call_analysis: {
      outcome: (c.outcome as string) ?? '',
      summary: (c.summary as string) ?? undefined,
      timeline,
      answers,
    },
    concatenated_transcript: (c.transcript as string) ?? undefined,
    recordingUrl: (c.recordingUrl as string) ?? undefined,
    flowName: (c.flowName as string) ?? undefined,
    flowType: (c.flowType as string) ?? undefined,
    transfer: (c.transfer as CallRecord['transfer']) ?? null,
  };
}

/**
 * Fetch the full call payload by id and adapt it into a CallRecord. For
 * callers (e.g. the hot-leads feed) that only hold a callId and want the
 * canonical transcript/timeline/recording rather than thin feed metadata.
 * Returns null when org/callId is missing or the call isn't found.
 */
export async function fetchCallRecordById(
  client: ApolloClient,
  organizationId: string | undefined,
  callId: string | undefined | null,
): Promise<CallRecord | null> {
  if (!organizationId || !callId) return null;
  const { data } = await client.query<{
    organization?: { calls?: { items?: Record<string, unknown>[] } } | null;
  }>({
    query: GET_ORG_CALL_BY_ID,
    variables: { organizationId, callId },
    fetchPolicy: 'cache-first',
  });
  const fresh = data?.organization?.calls?.items?.[0];
  return fresh ? adaptGqlCallToCallRecord(fresh) : null;
}
